import { useState } from "react";

import { Grid2, IconButton, Typography } from "@mui/material";
import moment from "moment";
import { FaChevronLeft } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";

import { formatTimeRange } from "../../../../../utils";
import { TLocation } from "../FlightLogs";
import { EditFlightDetail } from "./EditFlightDetail";
import { TFlightLog } from "../../../../../@types";
import { useAccount } from "../../../../../hooks/useAccount";

export type TFlightDetailProps = {
	flightLog: TFlightLog;
	gpsData: TLocation[];
	landing: number | null;
	distance: number | null;
	updateFlightLogData: (id: number, flightLog: TFlightLog) => void;
};

export function FlightDetail(props: TFlightDetailProps) {
	const navigate = useNavigate();
	const { account } = useAccount();
	type TState = {
		isEditing: boolean;
	};
	const [state, setState] = useState<TState>({
		isEditing: false,
	});

	const { dateText, timeText } = formatTimeRange(
		Number(props.flightLog.startTime),
		Number(props.flightLog.endTime),
	);
	const duration =
		Number(props.flightLog.endTime) - Number(props.flightLog.startTime);
	const hours = Math.floor(duration / (60 * 60 * 1000));
	const minutes = Math.round((duration - hours * 60 * 60 * 1000) / (60 * 1000));
	let durationText = "";
	if (hours === 1) {
		durationText += `${hours} hour`;
	} else if (hours > 1) {
		durationText += `${hours} hours`;
	}
	if (minutes === 1) {
		durationText += ` ${minutes} minute`;
	} else if (minutes > 1) {
		durationText += ` ${minutes} minutes`;
	}

	durationText = durationText.trim();
	if (durationText.length === 0) {
		durationText = "0 minutes";
	}

	if (state.isEditing) {
		return (
			<EditFlightDetail
				cancelEdit={() => {
					setState((prevState) => ({
						...prevState,
						isEditing: false,
					}));
				}}
				flightLog={props.flightLog}
				updateFlightLogData={props.updateFlightLogData}
			/>
		);
	}

	return (
		<Grid2
			size={{ xs: 12, sm: 12, md: 3, lg: 3, xl: 3 }}
			className="flight-log-detail">
			<div className="flight-log-detail-header">
				<IconButton
					onClick={() => {
						navigate("/flight-logs");
					}}>
					<FaChevronLeft className="normal-icon" />
				</IconButton>
				<Typography className="normal-text bold">{`${dateText}, ${timeText}`}</Typography>
			</div>
			<div className="flight-log-detail-content">
				<Grid2 container>
					<Grid2 size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }}>
						<Typography className="normal-text bold">Start</Typography>
					</Grid2>
					<Grid2 size={{ xs: 8, sm: 8, md: 8, lg: 8, xl: 8 }}>
						<Typography className="normal-text">
							{moment(Number(props.flightLog.startTime)).format(
								"HH:mm:ss, DD MMM YYYY",
							)}
						</Typography>
					</Grid2>
					<Grid2 size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }}>
						<Typography className="normal-text bold">End</Typography>
					</Grid2>
					<Grid2 size={{ xs: 8, sm: 8, md: 8, lg: 8, xl: 8 }}>
						<Typography className="normal-text">
							{moment(Number(props.flightLog.endTime)).format(
								"HH:mm:ss, DD MMM YYYY",
							)}
						</Typography>
					</Grid2>
					<Grid2 size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }}>
						<Typography className="normal-text bold">Flight time</Typography>
					</Grid2>
					<Grid2 size={{ xs: 8, sm: 8, md: 8, lg: 8, xl: 8 }}>
						<Typography className="normal-text">{durationText}</Typography>
					</Grid2>
					<div className="separator" />
					<Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
						<div className="flight-takeaways-header-container">
							<Typography className="normal-text bold">
								Flight Takeaways
							</Typography>

							{account?.email === props.flightLog.email && (
								<Typography
									className="normal-text edit-button"
									onClick={() => {
										setState((prevState) => ({
											...prevState,
											isEditing: true,
										}));
									}}>
									Edit
								</Typography>
							)}
						</div>
						{account?.email === props.flightLog.email && (
							<Typography whiteSpace="break-spaces" className="normal-text">
								{props.flightLog.description ||
									"How do you know what to do, if you don't remember what you did? Write takeaways."}
							</Typography>
						)}
						{account?.email !== props.flightLog.email && (
							<Typography whiteSpace="break-spaces" className="normal-text">
								{props.flightLog.description ||
									"User has not added any takeaways."}
							</Typography>
						)}
						{props.gpsData.length > 0 && (
							<div className="flight-takeaways-analytics-container">
								<div className="flight-takeaways-analytics-item">
									<Typography className="big-text bold">
										{`${(duration / (60 * 60 * 1000)).toFixed(1)} hrs`}
									</Typography>
									<Typography className="normal-text">In Flight</Typography>
								</div>
								<div className="vertical-line" />
								<div className="flight-takeaways-analytics-item">
									<Typography className="big-text bold">
										{`${((props.distance ?? 0) * 0.621371).toFixed(1)} mi`}
									</Typography>
									<Typography className="normal-text">Flown</Typography>
								</div>
								<div className="vertical-line" />
								<div className="flight-takeaways-analytics-item">
									<Typography className="big-text bold">
										{props.flightLog.landing || props.landing || 0}
									</Typography>
									<Typography className="normal-text">Landings</Typography>
								</div>
							</div>
						)}
					</Grid2>
				</Grid2>
			</div>
		</Grid2>
	);
}
